import { ClaimTransmissionStatusType } from '../../api/server/models/ClaimTransmissionStatusType';
import { ClaimWorkflowHistoryItem } from '../../api/server/models/ClaimWorkflowHistoryItem';
import { ProviderClaimPayment } from '../../api/server/models/ProviderClaimPayment';
import { denialPaymentTotals, IDenialPaymentRow, selectPaymentsForRow } from '../../services/denials/denial-payments';
import { buildDenialRemarkRows, IDenialRemarkRow } from '../../services/denials/denial-remark-rows';
import { blankRejections, buildRejectionRows, IRejectionRow } from '../../services/denials/rejection-rows';
import { denialsWeekLastDay, denialsWeekPhrase, DenialsWeekPreset, denialsWeekWindow, isDenialsWeekSelectable } from '../../services/denials/report-window';
import { formatDate } from '../../services/primitives/date-utils';
import { addDays } from 'date-fns';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { DenialsClaim, IDenialsClaimDetails } from '../../services/denials/denial-claim';
import { IDenialsApi } from '../../services/denials/denials-api';

/**
 * Demo mode's API (FE-181): generated remittances, loaded on first use so the generator is not in the bundle
 * every paying customer downloads. Webpack caches the chunk, so later calls resolve at once.
 */
const demoDenialsApi = (): Promise<IDenialsApi> =>
    import('../../services/demo/denials.demo').then(module => module.demoDenialsApi);
const rememberDemoCloseout = (claim: DenialsClaim): Promise<void> =>
    import('../../services/demo/denials.demo').then(module => module.rememberDemoCloseout(claim));

/**
 * The Denials & Rejections page's state, on either console: what differs between them — which endpoints —
 * comes in as `api` (FE-155). The table is over the adjustment-remark report.
 *
 * The range is a fixed seven days, which is the design's own rule ("Lookups are always 7 days") and
 * is also what the numbers ask for: the report costs about 11.7 s for a week on production, and it
 * returns the practice's whole adjustment surface rather than only denials. There is no wider range
 * to offer and no narrower one worth offering.
 */
/** Every transmission status type the report can be asked for, in the order the select lists them. */
export const REJECTION_STATUS_TYPES: ClaimTransmissionStatusType[] = [
    ClaimTransmissionStatusType.REJECTED,
    ClaimTransmissionStatusType.DENIED,
    ClaimTransmissionStatusType.ACKNOWLEDGED,
    ClaimTransmissionStatusType.FORWARDED,
    ClaimTransmissionStatusType.SUBMITTED,
    ClaimTransmissionStatusType.ADJUDICATED,
    ClaimTransmissionStatusType.COMPLETED,
];

/** First and last day of a loaded window, inclusive, `yyyy-MM-dd`. */
export interface IDenialsDays {
    first: string;
    last: string;
}

/** A report window's days: its `end` is exclusive, so the last day is the one before it. */
const daysOf = (window: { start: Date; end: Date }): IDenialsDays => ({
    first: formatDate(window.start, 'yyyy-MM-dd'),
    last: formatDate(addDays(window.end, -1), 'yyyy-MM-dd'),
});

export type RejectionStatusChoice = ClaimTransmissionStatusType | 'ALL';

export class DenialsStore {
    /** Which of the three range controls is active. */
    @observable public weekPreset: DenialsWeekPreset = 'thisWeek';
    /** The day the calendar chose, when the preset is `picked`. The window ENDS on this day. */
    @observable public pickedDay: Date | undefined;
    @observable public rows: IDenialRemarkRow[] = [];
    /** Report items that could not be read as a denial code. Surfaced, never silently dropped. */
    @observable public skipped: number = 0;
    @observable public loading: boolean = false;
    @observable public loaded: boolean = false;
    @observable public error: string | undefined;
    /** When the report on screen was requested — the page's "updated" stamp. Unset until one has loaded. */
    @observable public loadedAt: Date | undefined;
    /** The window the rows on screen actually came from, so the caption cannot drift from the data. */
    @observable public loadedPhrase: string | undefined;
    /**
     * The days the rows on screen came from, first and last, inclusive, as `yyyy-MM-dd`. "This week" is the seven
     * days ending today, so it moves at midnight; the export's filename must name the data's days, not today's.
     */
    @observable public loadedDays: IDenialsDays | undefined;

    /* ---- Rejections tab -------------------------------------------------------------------
     * Its own week, chosen the same way as the denials week (Jordan's v3: This week, Last week, Week
     * starting). The free From/To it replaces could ask for spans the report cannot answer — 180 days
     * failed outright on production while 30 worked — and seven-day windows never get near that.
     */
    @observable public rejectionWeekPreset: DenialsWeekPreset = 'thisWeek';
    @observable public rejectionPickedDay: Date | undefined;
    /** Which transmission status type to show. REJECTED is what the Rejection Worker evaluates. */
    @observable public rejectionType: RejectionStatusChoice = ClaimTransmissionStatusType.REJECTED;
    @observable public rejectionRows: IRejectionRow[] = [];
    /** Rejections the report returned with no message: counted, never shown or silently dropped. */
    @observable public rejectionBlank: number = 0;
    @observable public rejectionLoading: boolean = false;
    @observable public rejectionLoaded: boolean = false;
    @observable public rejectionError: string | undefined;
    /** The days the rejection rows on screen came from. */
    @observable public rejectionLoadedDays: IDenialsDays | undefined;

    /* ---- Drill-down: the payments behind one denials row ---------------------------------- */
    /** The row the drill-down was opened from. Unset means the table is showing. */
    @observable public drillRow: IDenialRemarkRow | undefined;
    @observable public drillPayments: IDenialPaymentRow[] = [];
    @observable public drillLoading: boolean = false;
    @observable public drillError: string | undefined;
    /** The payment list hit its safety cap: the drill-down shows the first rows only, and says so. */
    @observable public drillTruncated: boolean = false;
    /** The days the payments on screen were fetched for. */
    @observable public drillDays: IDenialsDays | undefined;

    /**
     * Payments already fetched, keyed by week and REASON CODE. Several rows share a reason code and
     * differ only by remark set (`CO:11` arrived as three rows), so one fetch serves all of them.
     */
    private paymentCache = new Map<string, { payments: ProviderClaimPayment[]; truncated: boolean }>();
    private drillRequestId = 0;

    /* ---- Claim drawer: one payment's claim, opened from the drill-down -------------------- */
    /** The payment the drawer was opened from. Unset means the drawer is closed. */
    @observable public drawerPayment: IDenialPaymentRow | undefined;
    /** `undefined` while resolving. `null` when the payment resolves to no claim — a real case. */
    @observable public drawerClaim: DenialsClaim | null | undefined;
    @observable public drawerDetails: IDenialsClaimDetails | undefined;
    @observable public drawerHistory: ClaimWorkflowHistoryItem[] | undefined;
    /** Details and history load after the claim, and separately: one failing does not blank the other. */
    @observable public drawerDetailsError: string | undefined;
    @observable public drawerHistoryError: string | undefined;
    @observable public drawerError: string | undefined;
    private drawerRequestId = 0;

    private service: IDenialsApi;
    /** Read live: the provider console turns demo mode on after its stores are built. */
    private features: { isDemo: boolean };
    /** Which load is the latest. A slower response for a week already left behind must not overwrite the table. */
    private requestId = 0;
    private rejectionRequestId = 0;

    constructor({ api, features }: { api: IDenialsApi; features: { isDemo: boolean } }) {
        this.service = api;
        this.features = features;
        makeObservable(this);
    }

    /** Whether the page shows generated remittances. The drawer asks, and so do the console's write flows (FE-187). */
    public get isDemo(): boolean {
        return this.features.isDemo;
    }

    /** Where the page's data comes from: the console's endpoints, or in demo mode the generated remittances. */
    private api = async (): Promise<IDenialsApi> => (this.features.isDemo ? demoDenialsApi() : this.service);

    /** The inclusive last day of the selected week. */
    @computed public get lastDay(): Date {
        return denialsWeekLastDay(this.weekPreset, new Date(), this.pickedDay);
    }

    /** `Jun 10 – Jun 16` — the inclusive span, never the exclusive end the request carries. */
    @computed public get phrase(): string {
        return denialsWeekPhrase(denialsWeekWindow(this.lastDay));
    }

    @action
    public setWeekPreset = (preset: DenialsWeekPreset): void => {
        if (preset === this.weekPreset && preset !== 'picked') {
            return;
        }
        this.weekPreset = preset;
        // The open row belongs to the previous week; leaving it up would show that week's payments
        // under a caption naming the new one.
        this.closeDrill();
        void this.load();
    };

    /** Choose the last day of the week to look at. A future day is refused rather than sent. */
    @action
    public pickWeek = (lastDay: Date, now: Date = new Date()): void => {
        if (!isDenialsWeekSelectable(lastDay, now)) {
            return;
        }
        this.pickedDay = lastDay;
        this.weekPreset = 'picked';
        this.closeDrill();
        void this.load(now);
    };

    @action
    public load = async (now: Date = new Date()): Promise<void> => {
        runInAction(() => {
            this.loading = true;
            this.error = undefined;
        });

        const lastDay = denialsWeekLastDay(this.weekPreset, now, this.pickedDay);
        const window = denialsWeekWindow(lastDay);
        const phrase = denialsWeekPhrase(window);
        const request = ++this.requestId;

        try {
            // `end` is the day AFTER the last day shown. The report's end is exclusive, so sending
            // the last day itself would ask for six days and quietly under-report the week.
            const items = await (await this.api()).fetchAdjustmentRemarkReport({
                start: formatDate(window.start, 'yyyy-MM-dd'),
                end: formatDate(window.end, 'yyyy-MM-dd'),
            });
            if (request !== this.requestId) {
                return;
            }
            const { rows, skipped } = buildDenialRemarkRows(items);
            runInAction(() => {
                this.rows = rows;
                this.skipped = skipped;
                this.loadedAt = now;
                this.loadedPhrase = phrase;
                this.loadedDays = daysOf(window);
                this.loaded = true;
                this.loading = false;
            });
        } catch (err) {
            if (request !== this.requestId) {
                return;
            }
            runInAction(() => {
                // The caption describes rows that are no longer on screen once a load fails, so it
                // goes with them (NS-3).
                this.rows = [];
                this.skipped = 0;
                this.loadedPhrase = undefined;
                this.loadedDays = undefined;
                this.error =
                    err instanceof Error ? err.message : 'Failed to load the adjustment report';
                this.loading = false;
                this.loaded = true;
            });
        }
    };

    /** How many claims the rejections table is summarising. */
    @computed public get rejectionClaims(): number {
        return this.rejectionRows.reduce((total, row) => total + row.claims, 0);
    }

    /** The inclusive last day of the rejections week. */
    @computed public get rejectionLastDay(): Date {
        return denialsWeekLastDay(this.rejectionWeekPreset, new Date(), this.rejectionPickedDay);
    }

    @computed public get rejectionPhrase(): string {
        return denialsWeekPhrase(denialsWeekWindow(this.rejectionLastDay));
    }

    @action
    public setRejectionWeekPreset = (preset: DenialsWeekPreset): void => {
        this.rejectionWeekPreset = preset;
        this.rejectionPickedDay = undefined;
        void this.loadRejections();
    };

    @action
    public pickRejectionWeek = (lastDay: Date, now: Date = new Date()): void => {
        if (!isDenialsWeekSelectable(lastDay, now)) {
            return;
        }
        this.rejectionPickedDay = lastDay;
        this.rejectionWeekPreset = 'picked';
        void this.loadRejections();
    };

    @action
    public setRejectionType = (type: RejectionStatusChoice): void => {
        this.rejectionType = type;
        void this.loadRejections();
    };

    @action
    public loadRejections = async (): Promise<void> => {
        runInAction(() => {
            this.rejectionLoading = true;
            this.rejectionError = undefined;
            // Cleared, not kept under the new choice: rows of one type or week shown beneath another read
            // as the new answer — a REJECTED message relabelled DENIED (Copilot, PR #581).
            this.rejectionRows = [];
            this.rejectionBlank = 0;
            this.rejectionLoadedDays = undefined;
        });

        const window = denialsWeekWindow(this.rejectionLastDay);
        // `end` is exclusive, like every other report here — the window already carries it that way.
        const start = formatDate(window.start, 'yyyy-MM-dd');
        const end = formatDate(window.end, 'yyyy-MM-dd');
        const type = this.rejectionType;
        const request = ++this.rejectionRequestId;
        try {
            // "All status types" asks for each type separately. The report row carries no type in the
            // client model, and the seven types partition the report exactly (measured 2026-09-30), so
            // per-type requests both tag every row and add up to the unfiltered whole.
            const types = type === 'ALL' ? REJECTION_STATUS_TYPES : [type];
            const service = await this.api();
            const results = await Promise.all(
                types.map(async t => ({ t, items: await service.fetchRejectionReport(start, end, t) })),
            );
            if (request !== this.rejectionRequestId) {
                return;
            }
            runInAction(() => {
                this.rejectionRows = results
                    .flatMap(({ t, items }) => buildRejectionRows(items, type === 'ALL' ? t : undefined))
                    .sort((a, b) => b.claims - a.claims || a.message.localeCompare(b.message));
                this.rejectionBlank = results.reduce((total, { items }) => total + blankRejections(items), 0);
                this.rejectionLoadedDays = daysOf(window);
                this.rejectionLoaded = true;
                this.rejectionLoading = false;
            });
        } catch (err) {
            if (request !== this.rejectionRequestId) {
                return;
            }
            runInAction(() => {
                this.rejectionRows = [];
                this.rejectionBlank = 0;
                this.rejectionLoadedDays = undefined;
                this.rejectionError =
                    err instanceof Error ? err.message : 'Failed to load the rejection report';
                this.rejectionLoading = false;
                this.rejectionLoaded = true;
            });
        }
    };

    @computed public get drillTotals(): { payments: number; charge: number } {
        return denialPaymentTotals(this.drillPayments);
    }

    @action
    public closeDrill = (): void => {
        // A fetch still in flight for the row being closed must not land on the next one.
        this.drillRequestId += 1;
        this.closeClaim();
        this.drillRow = undefined;
        this.drillPayments = [];
        this.drillTruncated = false;
        this.drillDays = undefined;
        this.drillError = undefined;
        this.drillLoading = false;
    };

    /** Open the payments behind a row. Each week-and-reason-code is fetched at most once. */
    @action
    public openDrill = async (row: IDenialRemarkRow): Promise<void> => {
        this.drillRow = row;
        this.drillPayments = [];
        this.drillTruncated = false;
        this.drillDays = undefined;
        this.drillError = undefined;

        const window = denialsWeekWindow(this.lastDay);
        const firstDay = formatDate(window.start, 'yyyy-MM-dd');
        // `filterPaymentDate` takes BT, read as inclusive at both ends, so this is the window's LAST
        // day — not the exclusive `end` the reports take.
        const lastDay = formatDate(addDays(window.end, -1), 'yyyy-MM-dd');
        // Keyed by mode too: payments fetched before demo mode was switched on must never answer inside it (NS-14).
        const key = `${this.features.isDemo ? 'demo' : 'live'}:${firstDay}:${lastDay}:${row.code.reasonCode}`;
        const request = ++this.drillRequestId;

        if (!this.paymentCache.has(key)) {
            this.drillLoading = true;
            try {
                const result = await (await this.api()).fetchPaymentsForCode(
                    row.code.reasonCode,
                    firstDay,
                    lastDay,
                );
                if (request !== this.drillRequestId) {
                    return;
                }
                this.paymentCache.set(key, result);
            } catch (err) {
                if (request !== this.drillRequestId) {
                    return;
                }
                runInAction(() => {
                    this.drillError =
                        err instanceof Error ? err.message : 'Failed to load the payments behind this row';
                    this.drillLoading = false;
                });
                return;
            }
        }

        runInAction(() => {
            const cached = this.paymentCache.get(key);
            this.drillPayments = selectPaymentsForRow(cached?.payments, row);
            this.drillTruncated = Boolean(cached?.truncated);
            this.drillDays = { first: firstDay, last: lastDay };
            this.drillLoading = false;
        });
    };

    /** What the closeout dialog saved, reflected without refetching the claim — as the claim page does. */
    @action
    public applyDrawerCloseout = (updated: DenialsClaim): void => {
        if (!this.drawerClaim) {
            return;
        }
        if (this.features.isDemo) {
            // Nothing was written, so the generator keeps it: the drawer reopens closed out (FE-187).
            void rememberDemoCloseout({ ...updated, id: this.drawerClaim.id });
        }
        this.drawerClaim = {
            ...this.drawerClaim,
            closeoutType: updated.closeoutType,
            closeoutDescription: updated.closeoutDescription,
            closeoutVoid: updated.closeoutVoid,
        };
    };

    @action
    public closeClaim = (): void => {
        this.drawerRequestId += 1;
        this.drawerPayment = undefined;
        this.drawerClaim = undefined;
        this.drawerDetails = undefined;
        this.drawerHistory = undefined;
        this.drawerError = undefined;
        this.drawerDetailsError = undefined;
        this.drawerHistoryError = undefined;
    };

    /**
     * Open the claim behind one drill-down payment.
     *
     * The claim comes first — it is the header, and its id is what the other two calls need. Details
     * (the heavy one, up to ~6 s) and history then load side by side, each into its own slot, so the
     * header and the activity are not held hostage by the codes.
     */
    @action
    public openClaim = async (payment: IDenialPaymentRow): Promise<void> => {
        this.closeClaim();
        this.drawerPayment = payment;
        const request = this.drawerRequestId;
        const current = (): boolean => request === this.drawerRequestId;
        const message = (err: unknown, fallback: string): string => (err instanceof Error ? err.message : fallback);

        let claim: DenialsClaim | undefined;
        let service: IDenialsApi;
        try {
            service = await this.api();
            claim = await service.fetchClaimForPayment(payment.id);
        } catch (err) {
            runInAction(() => {
                if (current()) {
                    this.drawerError = message(err, 'Failed to load the claim for this payment');
                }
            });
            return;
        }
        if (!current()) {
            return;
        }
        runInAction(() => {
            this.drawerClaim = claim || null;
        });
        if (!claim?.id) {
            return;
        }
        const claimId = claim.id;

        await Promise.all([
            service.fetchClaimDetails(claimId).then(
                details =>
                    runInAction(() => {
                        if (current()) {
                            this.drawerDetails = details || {};
                        }
                    }),
                err =>
                    runInAction(() => {
                        if (current()) {
                            this.drawerDetailsError = message(err, 'Failed to load the claim details');
                        }
                    }),
            ),
            service.fetchClaimHistory(claimId).then(
                history =>
                    runInAction(() => {
                        if (current()) {
                            this.drawerHistory = history || [];
                        }
                    }),
                err =>
                    runInAction(() => {
                        if (current()) {
                            this.drawerHistoryError = message(err, 'Failed to load the claim activity');
                        }
                    }),
            ),
        ]);
    };
}
